import { reactive, toRaw } from "vue"
import Farm from "@/farm/Farm"
import util from "@/util"
import globalState from "@/store/globalState"
import Cheatmode from "@/Cheatmode"

export default class SaveSystem {
  private state = reactive({ farm: new Farm(), hasFarm: false })

  private storageBuffer: any = undefined
  private cheatmode: Cheatmode

  constructor() {
    this.state.farm = new Farm()
    this.storageBuffer = this.loadAllFromStorage()
    this.cheatmode = new Cheatmode(this.state.farm as Farm)

    // Delete too old saves
    for (let saveName in this.storageBuffer) {
      if (this.storageBuffer[saveName].gameVersion < "0.5.0") this.deleteFromLocalStorage(saveName)
    }

    // @ts-ignore
    window.debug = () => {
      this.farm.state.lab.debug = true
      this.farm.state.money = 10000000
      return "Debug mode on"
    }

    // @ts-ignore
    window.doEvent = (choice: number) => {
      this.farm.state.eventManager.addEvent(this.farm.state.eventManager.randomEvent(this.farm, choice), this.farm)
      return "Event triggered"
    }

    this.autosave()
  }

  autosave() {
    if (this.hasFarm && this.farm.updateIsRunning) {
      let saveName = this.farm.state.saveName
      this.farm.state.saveName = "autosave"
      this.saveGame()
      this.farm.state.saveName = saveName
    }
    setTimeout(() => this.autosave(), 10000)
  }

  saveGame() {
    this.farm.state.saveDate = Date.now()
    this.savetoLocalStorage(this.state.farm.state)
  }

  loadGame(saveName: string) {
    let save = this.storageBuffer[saveName]
    if (save == undefined) {
      console.log("Could not load", saveName)
      return
    }
    this.useSave(save)
  }

  deleteGame() {
    this.deleteFromLocalStorage(this.state.farm.state.saveName)
    let saves = this.storageBuffer
    if (Object.keys(saves).length == 0) {
      //this.newGame()
      this.state.hasFarm = false
    } else {
      this.useSave(saves[Object.keys(saves)[0]])
    }
  }

  newGame() {
    this.state.farm = new Farm()
    let saveName = "Default Save"
    let i = 2
    while (saveName in this.allSaves) {
      saveName = "Default Save " + i++
    }
    this.farm.state.saveName = saveName
    this.saveGame()
    this.useSave(this.farm.state)
  }

  saveIsOutdated(saveName: string): boolean {
    let save = this.storageBuffer[saveName]
    if (save == undefined) return false
    if (save.gameVersion < '0.6.0') return true

    return false
  }

  exportSave() {
    util.download(`${this.farm.state.saveName}.cow`, this.stateToString(this.state.farm))
  }

  importSave(event: any) {
    let oldSaveName = this.farm.state.saveName

    let file = event.target.files[0]
    let reader = new FileReader()
    reader.addEventListener('load', (e) => {
      // @ts-ignore
      let text: string = e.target.result as string
      console.log("Importing save")
      this.useSave(JSON.parse(text).state)
      this.farm.state.saveName = oldSaveName
    })

    reader.readAsText(file)
  }

  get hasFarm(): boolean {
    return this.state.hasFarm
  }

  get farm(): Farm {
    return this.state.farm as Farm
  }

  get allSaves(): {[key: string]: any} {
    // Return all saves, but reorder so that autosave is always first
    let oldOrder = this.storageBuffer
    let newOrder: any = {}
    if (oldOrder.autosave) {
      newOrder.autosave = oldOrder.autosave
    }
    for (let saveName in oldOrder) {
      if (saveName == "autosave") continue
      newOrder[saveName] = oldOrder[saveName]
    }
    return newOrder
  }

  saveVersion(saveName: string) {
    let save = this.storageBuffer[saveName]
    if (save) return save.gameVersion

    return '0.0.0'
  }

  get gameVersion(): string {
    return new Farm().state.gameVersion
  }

  get currentGameIsSaved(): boolean {
    let saves = this.allSaves
    for (let saveName in saves) {
      let save = saves[saveName]
      let currentGame = this.state.farm.state
      if (currentGame.saveName == saveName && currentGame.time === save.time) {
        return true
      }
    }
    return false
  }

  get patchNotes() {
    return JSON.parse(globalState().patchNotesString.value)
  }

  private useSave(state: any) {
    this.state.farm = new Farm()
    this.state.farm.load(state)
    this.state.hasFarm = true
    this.cheatmode.farm = this.state.farm as Farm
  }

  private deleteFromLocalStorage(saveName: string) {
    delete this.storageBuffer[saveName]
    localStorage.setItem("milkfarm-saves", JSON.stringify(this.storageBuffer))
  }

  private stateToString(state: any): string {
    let milkfarmState = toRaw(state)
    return JSON.stringify(milkfarmState)
  }

  private savetoLocalStorage(state: any) {
    let milkfarmState = toRaw(state)
    this.storageBuffer[state.saveName] = milkfarmState
    localStorage.setItem("milkfarm-saves", JSON.stringify(this.storageBuffer))

    // The above milkfarmState assigns a reference to the non-reactive but real farm state to the storage buffer.
    // We don't want live references in the buffer, so we stringify and parse it to get rid of it.
    this.storageBuffer = JSON.parse(JSON.stringify(this.storageBuffer))
  }

  private loadAllFromStorage() {
    let savesString = localStorage.getItem("milkfarm-saves")
    let saves: {[key: string]: any} = {}
    if (savesString != null) {
      saves = JSON.parse(savesString)
    }
    return saves
  }
}
